// ============================================================================
// PatientStepPHI — Patient Information Form (local state only)
//
// Collects patient PHI locally and passes the data to the parent via onComplete.
// NO writes happen here — PHI is written via Cloud Functions inside handleSubmit
// in GateView after the referral is successfully committed to Firestore.
//
// PHI Architecture (Solution A):
//   - ALL PHI written via Cloud Functions (Admin SDK) — never direct Firestore
//   - Writes happen at submit time, using the referralId returned by submitReferral()
//   - This component is a pure controlled form with local validation only
//
// Props:
//   onComplete  (fn)  — called with { identity, contact, insurance } when valid
// ============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  User, AlertCircle, ChevronRight,
  Phone, CreditCard, Lock,
} from 'lucide-react';
import { PacketSuggestionsProvider, PacketSuggestionsNote, FieldSuggestions, SectionSuggestionControls, PacketPayerWarning } from './PacketSuggestions.jsx';
import { EhrSuggestionsProvider, EhrSuggestionsNote, EhrSectionControls, EhrFieldSuggestions } from './EhrSuggestions.jsx';
// Build 2N — shared, unit-tested address parser/normalizer + State <select> list.
import { US_STATES, parseAddress, canonicalAddress } from '../../core/addressParse.js';

// ── Constants ─────────────────────────────────────────────────────────────────

const SEX_OPTIONS = [
  { value: '',        label: 'Select...' },
  { value: 'male',    label: 'Male' },
  { value: 'female',  label: 'Female' },
  { value: 'other',   label: 'Other' },
  { value: 'unknown', label: 'Unknown / Prefer not to say' },
];

const IDENTIFIER_TYPES = [
  { value: 'MRN',      label: 'MRN' },
  { value: 'EHR',      label: 'EHR Chart #' },
  { value: 'EXTERNAL', label: 'External ID' },
];

// US_STATES (State <select> list) is now sourced from core/addressParse.js so the
// dropdown and the address parser share one canonical state table.

const PLAN_TYPES = [
  { value: '',       label: 'Select...' },
  { value: 'HMO',    label: 'HMO' },
  { value: 'PPO',    label: 'PPO' },
  { value: 'EPO',    label: 'EPO' },
  { value: 'POS',    label: 'POS' },
  { value: 'HDHP',   label: 'HDHP' },
  { value: 'OTHER',  label: 'Other' },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const FieldLabel = ({ label, required, hint }) => (
  <label className="block text-sm font-medium text-slate-300 mb-1.5">
    {label}
    {required && <span className="text-red-400 ml-1">*</span>}
    {hint && <span className="ml-2 text-xs font-normal text-slate-500">{hint}</span>}
  </label>
);

const FieldError = ({ msg }) =>
  msg ? <p className="mt-1 text-xs text-red-400">{msg}</p> : null;

const TextInput = ({ value, onChange, placeholder, type = 'text', ...props }) => (
  <input
    type={type}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 transition-colors"
    {...props}
  />
);

// ── Phone formatter ─────────────────────────────────────────────────────────
// Strips non-digits, caps at 10, returns progressive American format:
//   1-3  digits → "XXX"
//   4-6  digits → "(XXX) YYY"
//   7-10 digits → "(XXX) YYY-ZZZZ"
const formatPhone = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Address parser/normalizer (parseAddress, canonicalAddress) is shared from
// core/addressParse.js — see Build 2N. It strips a trailing country token so
// country never lands in the City field and normalizes the state to a postal code.

const SelectInput = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 appearance-none cursor-pointer pr-10"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
      <svg className="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, label, color = 'blue' }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className={`w-8 h-8 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
      <Icon className={`w-4 h-4 text-${color}-400`} />
    </div>
    <h3 className="text-base font-semibold text-white">{label}</h3>
  </div>
);

const OptionalToggle = ({ label, enabled, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`w-full flex items-center justify-between p-3.5 rounded-xl border text-left transition-colors ${
      enabled
        ? 'bg-slate-700/50 border-slate-600/70'
        : 'bg-slate-800/30 border-slate-700/40 hover:border-slate-600'
    }`}
  >
    <span className="text-sm text-slate-300">{label}</span>
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
      enabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'
    }`}>
      {enabled ? 'Added' : '+ Add'}
    </span>
  </button>
);

// ── Main component ────────────────────────────────────────────────────────────

const PatientStepPHI = ({ onComplete, initialData = null, packetId = null, ehrAutoFill = true }) => {
  // Patient identity — pre-populated from initialData when editing
  const [firstName, setFirstName] = useState(initialData?.identity?.name?.first || '');
  const [lastName,  setLastName]  = useState(initialData?.identity?.name?.last  || '');
  const [dob,       setDob]       = useState(initialData?.identity?.dob          || '');
  const [sex,       setSex]       = useState(initialData?.identity?.sex          || '');

  // Single identifier
  const [idType,  setIdType]  = useState(initialData?.identity?.identifiers?.[0]?.type  || 'MRN');
  const [idValue, setIdValue] = useState(initialData?.identity?.identifiers?.[0]?.value || '');

  // Contact
  const [phone, setPhone] = useState(formatPhone(initialData?.contact?.phone || ''));
  const [email, setEmail] = useState(initialData?.contact?.email || '');

  // Address (optional)
  const [showAddress, setShowAddress] = useState(Boolean(initialData?.contact?.address));
  const [street,      setStreet]      = useState(initialData?.contact?.address?.street || '');
  const [city,        setCity]        = useState(initialData?.contact?.address?.city   || '');
  const [addrState,   setAddrState]   = useState(initialData?.contact?.address?.state  || '');
  const [zip,         setZip]         = useState(initialData?.contact?.address?.zip    || '');

  // Insurance (optional)
  const [showInsurance, setShowInsurance] = useState(Boolean(initialData?.insurance));
  const [payerName,     setPayerName]     = useState(initialData?.insurance?.payerName   || '');
  const [payerId,       setPayerId]       = useState(initialData?.insurance?.payerId     || '');
  const [memberId,      setMemberId]      = useState(initialData?.insurance?.memberId    || '');
  const [groupNumber,   setGroupNumber]   = useState(initialData?.insurance?.groupNumber || '');
  const [planType,      setPlanType]      = useState(initialData?.insurance?.planType    || '');

  // Validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  // ── Phase 2B-2: OCR candidate field accessors (pending prefill model) ───────
  // The provider visually prefills these fields and tracks pending/confirmed
  // state. Prefill never becomes final PHI until the GP confirms (Use) + submits.
  const mapSex = (v) => {
    const s = String(v || '').trim().toUpperCase();
    if (s === 'M' || s === 'MALE') return 'male';
    if (s === 'F' || s === 'FEMALE') return 'female';
    if (s === 'U' || s === 'UNKNOWN') return 'unknown';
    return 'other';
  };
  const FORM_PLAN_TYPES = ['HMO', 'PPO', 'EPO', 'POS', 'HDHP'];
  const mapPlanType = (v) => {
    const s = String(v || '').trim().toUpperCase();
    return FORM_PLAN_TYPES.includes(s) ? s : 'OTHER';
  };

  // candidate value → the value the field will actually hold (post-transform).
  const transformForField = (fieldKey, value) => {
    const v = String(value == null ? '' : value).trim();
    switch (fieldKey) {
      case 'patient.name':       return v.replace(/\s+/g, ' ');
      case 'patient.sex':        return mapSex(v);
      case 'contact.phone':      return formatPhone(v);
      case 'contact.address':    return canonicalAddress(parseAddress(v));
      case 'insurance.planType': return mapPlanType(v);
      default:                   return v;
    }
  };
  // set the field to an ALREADY-transformed value (no further transform).
  const setFieldRaw = (fieldKey, formVal) => {
    switch (fieldKey) {
      case 'patient.name': {
        const parts = String(formVal).split(/\s+/).filter(Boolean);
        if (parts.length > 1) { setLastName(parts.pop()); setFirstName(parts.join(' ')); }
        else { setFirstName(formVal); setLastName(''); }
        break;
      }
      case 'patient.dateOfBirth': setDob(formVal); break;
      case 'patient.sex':         setSex(formVal); break;
      case 'patient.identifier':  setIdType('MRN'); setIdValue(formVal); break;
      case 'contact.phone':       setPhone(formVal); break;
      case 'contact.email':       setEmail(formVal); break;
      case 'contact.address': {
        const a = parseAddress(formVal);
        setShowAddress(true); setStreet(a.street); setCity(a.city); setAddrState(a.state); setZip(a.zip);
        break;
      }
      case 'insurance.payer':       setShowInsurance(true); setPayerName(formVal); break;
      case 'insurance.payerId':     setShowInsurance(true); setPayerId(formVal); break;
      case 'insurance.memberId':    setShowInsurance(true); setMemberId(formVal); break;
      case 'insurance.groupNumber': setShowInsurance(true); setGroupNumber(formVal); break;
      case 'insurance.planType':    setShowInsurance(true); setPlanType(formVal); break;
      default: break;
    }
  };
  const getField = (fieldKey) => {
    switch (fieldKey) {
      case 'patient.name':          return `${firstName} ${lastName}`.trim();
      case 'patient.dateOfBirth':   return dob;
      case 'patient.sex':           return sex;
      case 'patient.identifier':    return idValue;
      case 'contact.phone':         return phone;
      case 'contact.email':         return email;
      case 'contact.address':       return canonicalAddress({ street, city, state: addrState, zip });
      case 'insurance.payer':       return payerName;
      case 'insurance.payerId':     return payerId;
      case 'insurance.memberId':    return memberId;
      case 'insurance.groupNumber': return groupNumber;
      case 'insurance.planType':    return planType;
      default: return '';
    }
  };
  const clearField = (fieldKey) => {
    switch (fieldKey) {
      case 'patient.name':          setFirstName(''); setLastName(''); break;
      case 'patient.dateOfBirth':   setDob(''); break;
      case 'patient.sex':           setSex(''); break;
      case 'patient.identifier':    setIdValue(''); break;
      case 'contact.phone':         setPhone(''); break;
      case 'contact.email':         setEmail(''); break;
      case 'contact.address':       setStreet(''); setCity(''); setAddrState(''); setZip(''); break;
      case 'insurance.payer':       setPayerName(''); break;
      case 'insurance.payerId':     setPayerId(''); break;
      case 'insurance.memberId':    setMemberId(''); break;
      case 'insurance.groupNumber': setGroupNumber(''); break;
      case 'insurance.planType':    setPlanType(''); break;
      default: break;
    }
  };
  const isFieldEmpty = (fieldKey) => {
    const v = getField(fieldKey);
    return !String(v || '').trim();
  };

  // Provider reads accessors via this ref (always fresh — updated each render).
  const fieldsRef = useRef(null);
  fieldsRef.current = { get: getField, setRaw: setFieldRaw, transform: transformForField, clear: clearField, isEmpty: isFieldEmpty };

  // Build 2J P2 — the EhrSuggestionsProvider publishes its used-candidate collector
  // here; handleContinue reads it to attach never-PHI provenance (refs/counts only)
  // to the onComplete payload. Never carries a candidate value.
  const ehrProvenanceRef = useRef(null);

  // Unresolved pending prefill count — blocks Continue until Use/Dismiss.
  // OCR (PacketSuggestions) and EHR (EhrSuggestions) report independently; the
  // Continue blocker uses the combined total.
  const [ocrPending, setOcrPending] = useState(0);
  const [ehrPending, setEhrPending] = useState(0);
  const pendingCount = ocrPending + ehrPending;
  // Build 2O — EHR is the PRIMARY source for EHR-started packets. Defer OCR
  // (supporting-document) prefill until EHR has filled, so EHR wins the empty-field
  // race; OCR's existing isEmpty guard then never auto-overwrites an EHR-filled
  // field. For manual packets EHR loads no candidates and signals immediately, so
  // OCR proceeds unchanged.
  const [ehrLoaded, setEhrLoaded] = useState(false);
  const handleEhrLoaded = useCallback(() => setEhrLoaded(true), []);
  const [continueBlocked, setContinueBlocked] = useState(false);
  const handleOcrPendingChange = useCallback((n) => { setOcrPending(n); }, []);
  const handleEhrPendingChange = useCallback((n) => { setEhrPending(n); }, []);
  useEffect(() => { if (pendingCount === 0) setContinueBlocked(false); }, [pendingCount]);

  // Amendment 2: insurance details only persist if payerName is present.
  const insuranceNeedsPayer = showInsurance && !payerName.trim()
    && Boolean(memberId.trim() || groupNumber.trim() || payerId.trim() || planType);

  // ── Validation ──────────────────────────────────────────────────────────────

  const validate = () => {
    const errors = {};
    if (!firstName.trim()) errors.firstName = 'First name is required';
    if (!lastName.trim())  errors.lastName  = 'Last name is required';
    if (!dob)              errors.dob       = 'Date of birth is required';
    if (!sex)              errors.sex       = 'Sex is required';
    if (!idValue.trim())   errors.idValue   = 'At least one patient identifier is required';
    if (!phone.trim() && !email.trim()) {
      errors.contact = 'Phone number or email is required';
    }
    return errors;
  };

  // ── Continue handler — validate only, no writes ──────────────────────────

  const handleContinue = () => {
    // Block while OCR-prefilled fields are still pending (unconfirmed). Pending
    // OCR values must not silently become final PHI.
    if (pendingCount > 0) {
      setContinueBlocked(true);
      return;
    }
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const identity = {
      name: { first: firstName.trim(), last: lastName.trim() },
      dob,
      sex: sex || null,
      identifiers: [{ type: idType, value: idValue.trim() }],
    };

    const contact = {
      phone: phone.trim() || null,
      email: email.trim() || null,
      address: (showAddress && street.trim()) ? {
        street:   street.trim(),
        city:     city.trim()      || null,
        state:    addrState.trim() || null,
        zip:      zip.trim()       || null,
      } : null,
    };

    const insurance = (showInsurance && payerName.trim()) ? {
      payerName:   payerName.trim(),
      payerId:     payerId.trim()     || null,
      memberId:    memberId.trim()    || null,
      groupNumber: groupNumber.trim() || null,
      planType:    planType           || null,
    } : null;

    // Build 2J P2 — collect never-PHI EHR candidate usage (which EHR refs influenced
    // these fields) so Gate submit can record audit provenance. Refs/counts only.
    const ehrCandidateUsage = typeof ehrProvenanceRef.current === 'function'
      ? ehrProvenanceRef.current()
      : null;
    onComplete({ identity, contact, insurance, ehrCandidateUsage });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <PacketSuggestionsProvider packetId={packetId} fieldsRef={fieldsRef} onPendingChange={handleOcrPendingChange} deferUntil={packetId ? ehrLoaded : true}>
    <EhrSuggestionsProvider packetId={packetId} fieldsRef={fieldsRef} onPendingChange={handleEhrPendingChange} autoFill={ehrAutoFill} onLoaded={handleEhrLoaded} provenanceRef={ehrProvenanceRef}>
    <div className="space-y-6">

      {/* Phase 2B-2: packet suggestions disclaimer / load state (inline below) */}
      {packetId && <PacketSuggestionsNote />}
      {/* Build 1C: EHR suggestions disclaimer / load state */}
      {packetId && <EhrSuggestionsNote />}

      {/* PHI security notice */}
      <div className="flex items-center gap-3 p-3.5 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <Lock className="w-4 h-4 text-blue-400 flex-shrink-0" />
        <p className="text-xs text-blue-300">
          Patient information is encrypted and stored separately from clinical data.
          It will be saved when you submit the referral. Access is role-gated and audit-logged.
        </p>
      </div>

      {/* ── SECTION: Patient Identity ──────────────────────────────────────── */}
      <section>
        <SectionHeader icon={User} label="Patient Identity" color="emerald" />
        <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
          <SectionSuggestionControls section="patient" />
          <EhrSectionControls section="patient" />

          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel label="First Name" required />
              <TextInput value={firstName} onChange={setFirstName} placeholder="First name" />
              <FieldError msg={fieldErrors.firstName} />
            </div>
            <div>
              <FieldLabel label="Last Name" required />
              <TextInput value={lastName} onChange={setLastName} placeholder="Last name" />
              <FieldError msg={fieldErrors.lastName} />
            </div>
          </div>
          <FieldSuggestions fieldKey="patient.name" conflictOnly />
          <EhrFieldSuggestions fieldKey="patient.name" />

          {/* DOB + Sex row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel label="Date of Birth" required />
              <input
                type="date"
                value={dob}
                onChange={e => setDob(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500/50 cursor-pointer transition-colors"
              />
              <FieldError msg={fieldErrors.dob} />
              <FieldSuggestions fieldKey="patient.dateOfBirth" conflictOnly />
              <EhrFieldSuggestions fieldKey="patient.dateOfBirth" />
            </div>
            <div>
              <FieldLabel label="Sex" required />
              <SelectInput value={sex} onChange={setSex} options={SEX_OPTIONS} />
              <FieldError msg={fieldErrors.sex} />
              <FieldSuggestions fieldKey="patient.sex" conflictOnly />
              <EhrFieldSuggestions fieldKey="patient.sex" />
            </div>
          </div>

          {/* Identifier */}
          <div>
            <FieldLabel label="Patient Identifier" required hint="MRN, EHR chart #, or external ID" />
            <div className="flex gap-2">
              <div className="w-36 flex-shrink-0">
                <SelectInput value={idType} onChange={setIdType} options={IDENTIFIER_TYPES} />
              </div>
              <div className="flex-1">
                <TextInput
                  value={idValue}
                  onChange={setIdValue}
                  placeholder="Identifier value"
                />
              </div>
            </div>
            <FieldError msg={fieldErrors.idValue} />
            <FieldSuggestions fieldKey="patient.identifier" conflictOnly />
            <EhrFieldSuggestions fieldKey="patient.identifier" />
          </div>

        </div>
      </section>

      {/* ── SECTION: Contact ───────────────────────────────────────────────── */}
      <section>
        <SectionHeader icon={Phone} label="Contact" color="violet" />
        <div className="space-y-4 bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
          <SectionSuggestionControls section="contact" />
          <EhrSectionControls section="contact" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FieldLabel label="Phone" hint="at least one required" />
              <TextInput
                value={phone}
                onChange={val => setPhone(formatPhone(val))}
                placeholder="(555) 123-4567"
                type="tel"
              />
              <FieldSuggestions fieldKey="contact.phone" conflictOnly />
              <EhrFieldSuggestions fieldKey="contact.phone" />
            </div>
            <div>
              <FieldLabel label="Email" hint="at least one required" />
              <TextInput
                value={email}
                onChange={setEmail}
                placeholder="patient@example.com"
                type="email"
              />
              <FieldSuggestions fieldKey="contact.email" conflictOnly />
              <EhrFieldSuggestions fieldKey="contact.email" />
            </div>
          </div>
          <FieldError msg={fieldErrors.contact} />

          {/* Address toggle */}
          <OptionalToggle
            label="Address"
            enabled={showAddress}
            onToggle={() => setShowAddress(v => !v)}
          />

          {showAddress && (
            <div className="space-y-3 pt-1">
              <div>
                <FieldLabel label="Street Address" />
                <TextInput value={street} onChange={setStreet} placeholder="123 Main St" />
                <FieldSuggestions fieldKey="contact.address" conflictOnly />
                <EhrFieldSuggestions fieldKey="contact.address" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="col-span-1">
                  <FieldLabel label="City" />
                  <TextInput value={city} onChange={setCity} placeholder="City" />
                </div>
                <div>
                  <FieldLabel label="State" />
                  <SelectInput value={addrState} onChange={setAddrState} options={US_STATES} />
                </div>
                <div>
                  <FieldLabel label="ZIP" />
                  <TextInput value={zip} onChange={setZip} placeholder="ZIP" />
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* ── SECTION: Insurance (optional) ──────────────────────────────────── */}
      <section>
        <SectionHeader icon={CreditCard} label="Insurance" color="amber" />
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 space-y-3">

          <OptionalToggle
            label="Insurance Information"
            enabled={showInsurance}
            onToggle={() => setShowInsurance(v => !v)}
          />

          {showInsurance && (
            <div className="space-y-3 pt-1">
              <SectionSuggestionControls section="insurance" />
              <EhrSectionControls section="insurance" />
              <PacketPayerWarning needsPayer={insuranceNeedsPayer} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <FieldLabel label="Payer Name" required={showInsurance} />
                  <TextInput value={payerName} onChange={setPayerName} placeholder="e.g. Blue Cross" />
                  <FieldSuggestions fieldKey="insurance.payer" conflictOnly />
                  <EhrFieldSuggestions fieldKey="insurance.payer" />
                </div>
                <div>
                  <FieldLabel label="Payer ID" />
                  <TextInput value={payerId} onChange={setPayerId} placeholder="Payer ID" />
                  <FieldSuggestions fieldKey="insurance.payerId" conflictOnly />
                  <EhrFieldSuggestions fieldKey="insurance.payerId" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <FieldLabel label="Member ID" />
                  <TextInput value={memberId} onChange={setMemberId} placeholder="Member ID" />
                  <FieldSuggestions fieldKey="insurance.memberId" conflictOnly />
                  <EhrFieldSuggestions fieldKey="insurance.memberId" />
                </div>
                <div>
                  <FieldLabel label="Group Number" />
                  <TextInput value={groupNumber} onChange={setGroupNumber} placeholder="Group #" />
                  <FieldSuggestions fieldKey="insurance.groupNumber" conflictOnly />
                  <EhrFieldSuggestions fieldKey="insurance.groupNumber" />
                </div>
                <div>
                  <FieldLabel label="Plan Type" />
                  <SelectInput value={planType} onChange={setPlanType} options={PLAN_TYPES} />
                  <FieldSuggestions fieldKey="insurance.planType" conflictOnly />
                  <EhrFieldSuggestions fieldKey="insurance.planType" />
                </div>
              </div>
              <FieldSuggestions fieldKey="insurance.planName" conflictOnly />
              <EhrFieldSuggestions fieldKey="insurance.planName" />
            </div>
          )}

        </div>
      </section>

      {/* ── Pending packet suggestions blocker ──────────────────────────────── */}
      {continueBlocked && pendingCount > 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-300">
            Use or dismiss packet suggestions before continuing.
            {' '}({pendingCount} pending {pendingCount === 1 ? 'field' : 'fields'})
          </p>
        </div>
      )}

      {/* ── Validation errors summary ───────────────────────────────────────── */}
      {Object.keys(fieldErrors).length > 0 && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">Please fix the errors above before continuing.</p>
        </div>
      )}

      {/* ── Continue button ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-700/30">
        <p className="text-xs text-slate-500">
          Patient info will be saved when the referral is submitted.
        </p>
        <button
          type="button"
          onClick={handleContinue}
          className="flex items-center gap-2 px-7 py-3 rounded-xl text-white font-semibold bg-emerald-500 hover:bg-emerald-600 transition-colors"
        >
          <ChevronRight className="w-5 h-5" />Continue
        </button>
      </div>

    </div>
    </EhrSuggestionsProvider>
    </PacketSuggestionsProvider>
  );
};

export default PatientStepPHI;
